/**
 * Semantic colour decisions.
 *
 * Not a palette — the palette is CSS custom properties in `index.css` and
 * they re-theme automatically. This module answers *which* token a value
 * should wear, so that "a loss is red" is decided once instead of in every
 * table cell.
 *
 * The governing rule of this file, and of the whole UI: **colour means
 * direction, nothing else.** Buttons, links and focus rings use `accent`.
 * The moment a button is green, green stops meaning "up".
 *
 * Two weights of each direction colour exist, and picking the wrong one is
 * a real accessibility failure rather than a nitpick:
 *   `--up`      / `--down`      graphical marks — WCAG 1.4.11, 3:1
 *   `--up-text` / `--down-text` numbers and labels — WCAG 1.4.3, 4.5:1
 */

import type { AlertSeverity, DeploymentStatus, Environment, LifecycleStage } from '@/api/types';

export type Tone =
  | 'neutral'
  | 'up'
  | 'down'
  | 'accent'
  | 'warn'
  | 'danger'
  | 'info'
  | 'muted';

/** Zero is neutral, not green. A flat day is not a winning day. */
export function toneForValue(value: number | null | undefined): Tone {
  if (value === null || value === undefined || !Number.isFinite(value)) return 'muted';
  if (value > 0) return 'up';
  if (value < 0) return 'down';
  return 'neutral';
}

/** Tailwind text class for a signed number. Uses the *text* weight. */
export function pnlTextClass(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return 'text-lo';
  if (value > 0) return 'text-up-text';
  if (value < 0) return 'text-down-text';
  return 'text-mid';
}

/** Tailwind background class for a signed number — row tints, bars, chips. */
export function pnlBgClass(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return 'bg-transparent';
  if (value > 0) return 'bg-up-weak';
  if (value < 0) return 'bg-down-weak';
  return 'bg-transparent';
}

/**
 * Border colour for a win/loss row marker.
 *
 * A 3px left border rather than a tinted row background: at thirty rows on
 * screen, tinted backgrounds destroy legibility and steal the hover and
 * selected states, which then have nowhere to go.
 */
export function pnlBorderVar(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value) || value === 0) {
    return 'var(--flat)';
  }
  return value > 0 ? 'var(--up)' : 'var(--down)';
}

/** Raw CSS variable reference for a signed value, for inline styles. */
export function pnlColorVar(value: number | null | undefined, weight: 'mark' | 'text' = 'text'): string {
  if (value === null || value === undefined || !Number.isFinite(value) || value === 0) {
    return 'var(--text-lo)';
  }
  if (weight === 'mark') return value > 0 ? 'var(--up)' : 'var(--down)';
  return value > 0 ? 'var(--up-text)' : 'var(--down-text)';
}

export const TONE_TEXT: Record<Tone, string> = {
  neutral: 'text-mid',
  muted: 'text-lo',
  up: 'text-up-text',
  down: 'text-down-text',
  accent: 'text-accent',
  warn: 'text-warn',
  danger: 'text-danger',
  info: 'text-info',
};

export const TONE_BG: Record<Tone, string> = {
  neutral: 'bg-surface-2',
  muted: 'bg-surface-2',
  up: 'bg-up-weak',
  down: 'bg-down-weak',
  accent: 'bg-accent-weak',
  warn: 'bg-warn-weak',
  danger: 'bg-danger-weak',
  info: 'bg-info-weak',
};

export const TONE_BORDER: Record<Tone, string> = {
  neutral: 'border-border',
  muted: 'border-border',
  up: 'border-up',
  down: 'border-down',
  accent: 'border-accent',
  warn: 'border-warn',
  danger: 'border-danger',
  info: 'border-info',
};

// ── Domain mappings ───────────────────────────────────────────────────

/**
 * Environment.
 *
 * LIVE is `danger` — deliberately the loudest thing on the screen. This is
 * the only visual cue distinguishing a process that can move real money
 * from one that cannot, and it must never be subtle.
 */
export function toneForEnvironment(environment: Environment): Tone {
  switch (environment) {
    case 'live':
      return 'danger';
    case 'paper':
      return 'info';
    case 'testnet':
      return 'warn';
    default:
      return 'neutral';
  }
}

export function labelForEnvironment(environment: Environment): string {
  return environment.toUpperCase();
}

/**
 * Deployment status.
 *
 * HALTED is `danger`, not `warn`: STOPPED is a decision, HALTED is the
 * kill-switch. Rendering them the same way hides a breach.
 */
export function toneForDeploymentStatus(status: DeploymentStatus): Tone {
  switch (status) {
    case 'running':
      return 'up';
    case 'paused':
      return 'warn';
    case 'stopped':
      return 'muted';
    case 'halted':
      return 'danger';
    default:
      return 'neutral';
  }
}

export function toneForAlertSeverity(severity: AlertSeverity): Tone {
  switch (severity) {
    case 'critical':
      return 'danger';
    case 'warning':
      return 'warn';
    case 'info':
      return 'info';
    default:
      return 'neutral';
  }
}

export function toneForLifecycleStage(stage: LifecycleStage): Tone {
  switch (stage) {
    case 'live':
      return 'danger';
    case 'paper':
      return 'info';
    case 'promoted':
      return 'up';
    case 'tested':
      return 'accent';
    case 'draft':
      return 'muted';
    case 'retired':
      return 'muted';
    default:
      return 'neutral';
  }
}

/**
 * Exit reasons.
 *
 * `stop_loss` is drawn in the loss colour even when a particular stop
 * saved money, because the aggregate question this colouring answers is
 * "what is closing my trades" — and a stop-dominated exit table is the
 * strategy telling you something no Sharpe ratio will.
 *
 * A stop that had been moved to breakeven is the one stop-out that is not
 * that, and it is checked FIRST because every string below also contains
 * "stop". [F6] It is the loss the strategy declined to take: −0.157R against
 * −1.148R for the trades whose stop never moved, measured on the owner's own
 * run. Painting those the same colour is what made 53 working breakeven moves
 * indistinguishable from 50 full stop-outs.
 */
export function toneForExitReason(reason: string): Tone {
  const key = reason.toLowerCase();
  if (key.includes('breakeven') || key.includes('break_even')) return 'accent';
  if (key.includes('stop')) return 'down';
  if (key.includes('kill') || key.includes('halt') || key.includes('emergency')) return 'danger';
  if (key.includes('target') || key.includes('take_profit') || key.includes('roi')) return 'up';
  if (key.includes('time') || key.includes('expiry') || key.includes('barrier')) return 'warn';
  if (key.includes('signal')) return 'accent';
  return 'neutral';
}

/**
 * Protection status — the highest-stakes colour decision in the app.
 *
 * `is_fully_protected === false` means an open position's resting stops do
 * not cover its whole size. There is no amber middle ground here on
 * purpose: partially protected is unprotected.
 */
export function toneForProtection(isFullyProtected: boolean): Tone {
  return isFullyProtected ? 'up' : 'danger';
}

/**
 * Drawdown, as a **fraction** of the configured limit — `0.75` means three
 * quarters of the budget is gone, not 0.75%.
 *
 * Escalates rather than jumping straight to red, so the user can see
 * pressure building instead of only learning about it at the breach. It
 * goes red at three quarters, not at the breach: by the time the limit is
 * hit the decision has already been taken out of his hands, and the point
 * of the colour is to be seen while there is still something to decide.
 */
export function toneForDrawdownUsage(usedFraction: number): Tone {
  if (!Number.isFinite(usedFraction)) return 'muted';
  if (usedFraction >= 0.75) return 'danger';
  if (usedFraction >= 0.5) return 'warn';
  return 'up';
}

// ── Series colours ────────────────────────────────────────────────────

/** The six-colour, colourblind-safe indicator ramp, as CSS var refs. */
export const SERIES_VARS = [
  'var(--s1)',
  'var(--s2)',
  'var(--s3)',
  'var(--s4)',
  'var(--s5)',
  'var(--s6)',
] as const;

/** Stable colour for series `index`, wrapping around the ramp. */
export function seriesVar(index: number): string {
  const slot = ((index % SERIES_VARS.length) + SERIES_VARS.length) % SERIES_VARS.length;
  return SERIES_VARS[slot] ?? SERIES_VARS[0];
}

/**
 * Deterministic ramp slot for a string key.
 *
 * So a strategy or symbol keeps the same colour across renders, pages and
 * reloads — a legend whose colours shuffle on every fetch is worse than no
 * legend.
 */
export function seriesVarFor(key: string): string {
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = (hash * 31 + key.charCodeAt(i)) | 0;
  }
  return seriesVar(Math.abs(hash));
}
